import { lazy, Suspense, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";

const Auth = lazy(() => import("./pages/Auth"));
const AceitarConvite = lazy(() => import("./pages/AceitarConvite"));
const RecuperarSenha = lazy(() => import("./pages/RecuperarSenha"));
const RedefinirSenha = lazy(() => import("./pages/RedefinirSenha"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Vendas = lazy(() => import("./pages/Vendas"));
const Clientes = lazy(() => import("./pages/Clientes"));
const Produtos = lazy(() => import("./pages/Produtos"));
const Configuracoes = lazy(() => import("./pages/Configuracoes"));
const Fidelidade = lazy(() => import("./pages/Fidelidade"));
const LiveShop = lazy(() => import("./pages/LiveShop"));
const Mensagens = lazy(() => import("./pages/Mensagens"));
const LiveExecutarPage = lazy(() => import("./pages/LiveExecutarPage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Portal = lazy(() => import("./pages/Portal"));
const Ajuda = lazy(() => import("./pages/Ajuda"));
const AjudaTutoriais = lazy(() => import("./pages/AjudaTutoriais"));
const AjudaArtigo = lazy(() => import("./pages/AjudaArtigo"));
const InstagramCallback = lazy(() => import("./pages/InstagramCallback"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const Automacoes = lazy(() => import("./pages/Automacoes"));
const Integracoes = lazy(() => import("./pages/Integracoes"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminTenants = lazy(() => import("./pages/admin/AdminTenants"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminFinanceiro = lazy(() => import("./pages/admin/AdminFinanceiro"));
const AdminHelpCenter = lazy(() => import("./pages/admin/AdminHelpCenter"));
const AdminHelpArticleForm = lazy(() => import("./pages/admin/AdminHelpArticleForm"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogPostForm = lazy(() => import("./pages/admin/AdminBlogPostForm"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const SacolinhasPage = lazy(() =>
  import("./plugins/sacolinhas/PageComponent").then((m) => ({ default: m.SacolinhasPage }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const Protected = ({ children }: { children: ReactNode }) => (
  <ProtectedRoute>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

const Admin = ({ children }: { children: ReactNode }) => (
  <ProtectedRoute>
    <AdminGuard>
      <AdminLayout>{children}</AdminLayout>
    </AdminGuard>
  </ProtectedRoute>
);

function AppRoutes() {
  const { pathname } = useLocation();

  return (
    <ErrorBoundary resetKey={pathname}>
      <Suspense fallback={<PageLoader />}>
        <AuthProvider>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/instagram-callback" element={<InstagramCallback />} />
            <Route path="/auth/oauth-callback" element={<OAuthCallback />} />
            <Route path="/recuperar-senha" element={<RecuperarSenha />} />
            <Route path="/redefinir-senha" element={<RedefinirSenha />} />
            <Route path="/aceitar-convite" element={<AceitarConvite />} />
            <Route path="/checkout/:sessionId" element={<Checkout />} />
            <Route path="/portal" element={<Portal />} />

            <Route path="/ajuda" element={<Protected><Ajuda /></Protected>} />
            <Route path="/ajuda/tutoriais" element={<Protected><AjudaTutoriais /></Protected>} />
            <Route path="/ajuda/:categorySlug/:articleSlug" element={<Protected><AjudaArtigo /></Protected>} />

            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/vendas" element={<Protected><Vendas /></Protected>} />
            <Route path="/clientes" element={<Protected><Clientes /></Protected>} />
            <Route path="/fidelidade" element={<Protected><Fidelidade /></Protected>} />
            <Route path="/produtos" element={<Protected><Produtos /></Protected>} />

            <Route path="/configuracoes" element={<Protected><Configuracoes /></Protected>} />
            <Route path="/configuracoes/envios" element={<Protected><Configuracoes /></Protected>} />
            <Route path="/configuracoes/checkout" element={<Protected><Configuracoes /></Protected>} />
            <Route path="/configuracoes/integracoes" element={<Protected><Configuracoes /></Protected>} />
            <Route path="/configuracoes/plugins" element={<Protected><Configuracoes /></Protected>} />
            <Route path="/configuracoes/plugins/:pluginSlug" element={<Protected><Configuracoes /></Protected>} />
            <Route path="/configuracoes/assinatura" element={<Protected><Configuracoes /></Protected>} />

            <Route path="/mensagens" element={<Protected><Mensagens /></Protected>} />
            <Route path="/automacoes" element={<Protected><Automacoes /></Protected>} />
            <Route path="/integracoes" element={<Protected><Integracoes /></Protected>} />
            <Route path="/lives" element={<Protected><LiveShop /></Protected>} />
            <Route path="/lives/executar" element={<Protected><LiveExecutarPage /></Protected>} />
            <Route path="/sacolinhas" element={<Protected><SacolinhasPage /></Protected>} />

            <Route path="/a12o" element={<Admin><AdminDashboard /></Admin>} />
            <Route path="/a12o/tenants" element={<Admin><AdminTenants /></Admin>} />
            <Route path="/a12o/assinaturas" element={<Admin><AdminSubscriptions /></Admin>} />
            <Route path="/a12o/financeiro" element={<Admin><AdminFinanceiro /></Admin>} />
            <Route path="/a12o/planos" element={<Admin><AdminPlans /></Admin>} />
            <Route path="/a12o/ajuda" element={<Admin><AdminHelpCenter /></Admin>} />
            <Route path="/a12o/ajuda/novo" element={<Admin><AdminHelpArticleForm /></Admin>} />
            <Route path="/a12o/ajuda/:id/editar" element={<Admin><AdminHelpArticleForm /></Admin>} />
            <Route path="/a12o/blog" element={<Admin><AdminBlog /></Admin>} />
            <Route path="/a12o/blog/new" element={<Admin><AdminBlogPostForm /></Admin>} />
            <Route path="/a12o/blog/:id/edit" element={<Admin><AdminBlogPostForm /></Admin>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </Suspense>
    </ErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
